import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Bot, ChevronDown, ChevronUp, Clock, Loader2, ExternalLink } from 'lucide-react';

const AGENT_LABELS = {
  scout_agent: { name: 'Scout', role: 'Ricerca trend e fonti' },
  copywriter_agent: { name: 'Copywriter', role: 'Produzione testi' },
  planner_agent: { name: 'Planner', role: 'Pianificazione calendario' },
  crm_agent: { name: 'CRM', role: 'Gestione contatti e score' },
  offering_agent: { name: 'Offering', role: 'Catalogo e coerenza offerta' },
  events_agent: { name: 'Evento', role: 'Ciclo completo eventi' },
  funnel_agent: { name: 'Funnel', role: 'Sequenze di nurturing' },
  outreach_executor: { name: 'Outreach', role: 'Invio comunicazioni' },
  analyst_agent: { name: 'Analyst', role: 'Report e metriche' },
  ads_monitor_agent: { name: 'Ads Monitor', role: 'Performance advertising' },
  sales_agent: { name: 'Sales', role: 'Pipeline e proposte' },
  designer_agent: { name: 'Designer', role: 'Asset grafici' },
  partnership_agent: { name: 'Partnership', role: 'Network e riattivazioni' },
  meta_agent: { name: 'Meta', role: 'Ottimizzazione sistema' },
  strategist_agent: { name: 'Strategist', role: 'Analisi strategica' },
};

function timeAgo(d) {
  if (!d) return 'mai';
  const m = Math.floor((Date.now() - new Date(d).getTime()) / 60000);
  if (m < 60) return `${m}min fa`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h fa`;
  return `${Math.floor(h / 24)}gg fa`;
}

export default function AgentActivityPanel({ agents = [], areaLabel = '' }) {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!agents.length) { setLoading(false); return; }
    api.get('/agents/activity-feed', { params: { agents: agents.join(','), limit: 8, hours: 72 } })
      .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [agents.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return (
    <div className="flex items-center gap-2 px-4 py-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg mb-4" data-testid="agent-panel-loading">
      <Loader2 className="w-3.5 h-3.5 animate-spin text-[#64748B]" />
      <span className="text-xs text-[#64748B]">Caricamento agenti...</span>
    </div>
  );
  if (!data) return null;

  const totalPending = data.agents_status.reduce((s, a) => s + (a.pending_approvals || 0), 0);

  return (
    <div className="border border-[#E2E8F0] rounded-lg mb-4 bg-white overflow-hidden" data-testid="agent-activity-panel">
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-[#F8FAFC] transition-colors" data-testid="agent-panel-toggle">
        <div className="flex items-center gap-3">
          <Bot className="w-4 h-4 text-[#2E75B6]" />
          <span className="text-xs font-semibold text-[#1A3A5C]">Agenti AI — {areaLabel}</span>
          <div className="flex items-center gap-1">
            {data.agents_status.map(a => {
              const info = AGENT_LABELS[a.agent_name] || { name: a.agent_name };
              return (
                <div key={a.agent_name} className="flex items-center gap-1 px-1.5 py-0.5 bg-[#F1F5F9] rounded text-[10px] text-[#64748B]" title={`${info.name}: ${a.last_action || 'Nessuna attivita'}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${a.output_count_24h > 0 ? 'bg-emerald-500' : a.is_active ? 'bg-amber-400' : 'bg-slate-300'}`} />
                  {info.name}
                </div>
              );
            })}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {totalPending > 0 && <Badge className="bg-amber-100 text-amber-700 text-[10px] h-5">{totalPending} in approvazione</Badge>}
          {expanded ? <ChevronUp className="w-3.5 h-3.5 text-[#94A3B8]" /> : <ChevronDown className="w-3.5 h-3.5 text-[#94A3B8]" />}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-[#E2E8F0] px-4 py-3 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {data.agents_status.map(a => {
              const info = AGENT_LABELS[a.agent_name] || { name: a.agent_name, role: '' };
              return (
                <div key={a.agent_name} className="p-2.5 bg-[#F8FAFC] rounded-lg border border-[#F1F5F9]" data-testid={`agent-status-${a.agent_name}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-[#1A3A5C]">{info.name}</span>
                    <span className={`w-2 h-2 rounded-full ${a.output_count_24h > 0 ? 'bg-emerald-500' : a.is_active ? 'bg-amber-400' : 'bg-slate-300'}`} />
                  </div>
                  <p className="text-[10px] text-[#64748B]">{info.role}</p>
                  <div className="flex items-center gap-1 mt-1">
                    <Clock className="w-2.5 h-2.5 text-[#94A3B8]" />
                    <span className="text-[10px] text-[#94A3B8]">Ultimo: {timeAgo(a.last_activity)}</span>
                  </div>
                  <p className="text-[10px] text-[#475569] truncate mt-0.5">{a.last_action}</p>
                  <div className="flex items-center gap-2 mt-1.5 text-[10px]">
                    <span className="text-[#64748B]">24h: <strong>{a.output_count_24h}</strong> output</span>
                    {a.pending_approvals > 0 && (
                      <button onClick={(e) => { e.stopPropagation(); window.location.href = '/approval'; }} className="text-amber-600 hover:underline flex items-center gap-0.5">
                        {a.pending_approvals} da approvare <ExternalLink className="w-2.5 h-2.5" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {data.activities.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold text-[#64748B] uppercase mb-1.5">Attivita recenti</p>
              <div className="space-y-1">
                {data.activities.map((act, i) => {
                  const info = AGENT_LABELS[act.agent_name] || { name: act.agent_name };
                  return (
                    <div key={act.id || i} className="flex items-center gap-2 text-[10px] py-1 border-b border-[#F1F5F9] last:border-0">
                      <Badge variant="outline" className="text-[9px] px-1 py-0 h-4 flex-shrink-0">{info.name}</Badge>
                      <span className="text-[#475569] truncate">{act.description || act.summary || act.action_type}</span>
                      <span className="text-[#94A3B8] flex-shrink-0 ml-auto">{timeAgo(act.created_at)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <button onClick={() => { window.location.href = '/agents'; }} className="text-[10px] text-[#2E75B6] hover:underline flex items-center gap-1" data-testid="agents-hub-link">
            Vai al Team Agenti per configurazione completa <ExternalLink className="w-2.5 h-2.5" />
          </button>
        </div>
      )}
    </div>
  );
}
